import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';
import { ChildrenService } from '../../../shared/services/childrenService';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableEmptyState,
  TableBodySkeleton,
} from '../../../shared/components/ui';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const STATUS_STYLES = {
  UPCOMING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  DUE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  OVERDUE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const PlanStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status}
  </span>
);

export default function ChildVaccinationPlanPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const darkMode = useSelector(selectDarkMode);

  const [child, setChild] = useState(null);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ChildrenService.getVaccinationPlan(childId);
      const data = res?.data ?? res;
      setChild(data?.child ?? null);
      setStats(data?.stats ?? null);
      // API returns planByAgeGroup: [{ ageGroupLabel, items: [...] }]
      const byAgeGroup = data?.planByAgeGroup ?? [];
      setGroups(byAgeGroup.map(g => [g.ageGroupLabel, g.items]));
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load vaccination plan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [childId]);

  const statCards = stats
    ? [
        { label: 'Total Doses', value: stats.total, color: 'blue' },
        { label: 'Action Needed', value: (stats.due ?? 0) + (stats.overdue ?? 0), color: ((stats.due ?? 0) + (stats.overdue ?? 0)) > 0 ? 'red' : 'gray' },
        { label: 'Completed', value: stats.completed, color: 'green' },
      ]
    : [];

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/children')}
            className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Children</span>
          </button>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
              <ClipboardDocumentCheckIcon className={`w-8 h-8 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Vaccination Plan
              </h1>
              {child && (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {child.name} · DOB: {formatDate(child.dateOfBirth)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        {!loading && !error && statCards.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            {statCards.map(({ label, value, color }) => (
              <div key={label} className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                <p className={`text-2xl font-bold text-${color}-600 dark:text-${color}-400`}>{value}</p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Timeline groups */}
        {loading ? (
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>Vaccine</TableHeaderCell>
                <TableHeaderCell>Dose</TableHeaderCell>
                <TableHeaderCell>Scheduled Window</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Completed Date</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              <TableBodySkeleton rows={6} columns={6} />
            </TableBody>
          </Table>
        ) : groups.length === 0 ? (
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>Vaccine</TableHeaderCell>
                <TableHeaderCell>Dose</TableHeaderCell>
                <TableHeaderCell>Scheduled Window</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Completed Date</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              <TableEmptyState
                icon={ClipboardDocumentCheckIcon}
                title="No vaccination plan found"
                message="This child does not have a vaccination plan yet."
              />
            </TableBody>
          </Table>
        ) : (
          <div className="space-y-6">
            {groups.map(([ageGroupLabel, items]) => (
              <div key={ageGroupLabel} className={`rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Age group header */}
                <div className={`px-5 py-3 flex items-center gap-2 ${darkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}`}>
                  <CalendarIcon className="w-4 h-4 text-[#4057fe]" />
                  <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{ageGroupLabel}</h3>
                  <span className={`ml-auto text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {items.filter(i => i.status === 'COMPLETED').length}/{items.length} completed
                  </span>
                </div>

                <Table>
                  <TableHead>
                    <tr>
                      <TableHeaderCell>Vaccine</TableHeaderCell>
                      <TableHeaderCell>Dose</TableHeaderCell>
                      <TableHeaderCell>Scheduled Window</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Completed Date</TableHeaderCell>
                      <TableHeaderCell>Action</TableHeaderCell>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {item.vaccine?.name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                            Dose {item.doseNumber}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {formatDate(item.scheduledFromDate)} – {formatDate(item.scheduledToDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <PlanStatusBadge status={item.status} />
                        </TableCell>
                        <TableCell>
                          {item.completedDate
                            ? <span className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{formatDate(item.completedDate)}</span>
                            : <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                          }
                        </TableCell>
                        <TableCell>
                          {(item.status === 'DUE' || item.status === 'OVERDUE') && (
                            <button
                              type="button"
                              onClick={() => navigate('/admin/Appointments/add')}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Add Appointment
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
